/**
 * Dynamic Product Grid Padding
 * Detects the first card in each row and applies appropriate padding
 * for edge-to-edge product grids
 */

class ProductGridDynamicPadding {
  constructor() {
    this.grids = [];
    this.resizeObserver = null;
    this.resizeTimeout = null;
    
    // CSS class names
    this.FIRST_IN_ROW_CLASS = 'product-grid__item--first-in-row';
    this.FULL_WIDTH_WRAPPER_CLASS = 'collection-wrapper--full-width';
    this.FULL_WIDTH_MOBILE_CLASS = 'collection-wrapper--full-width-on-mobile';
    
    this.init();
  }

  init() {
    // Find all product grids
    this.findProductGrids();
    
    // Set up resize observer for responsive behavior
    this.setupResizeObserver();
    
    // Initial calculation
    this.updateAllGrids();
    
    // Listen for dynamic content changes (e.g., filters, load more)
    this.observeGridChanges();
  }

  findProductGrids() {
    // Find grids within full-width collection wrappers
    const fullWidthWrappers = document.querySelectorAll(
      `.${this.FULL_WIDTH_WRAPPER_CLASS} .product-grid, 
       .${this.FULL_WIDTH_MOBILE_CLASS} .product-grid`
    );
    
    this.grids = Array.from(fullWidthWrappers);
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) return;
    
    this.resizeObserver = new ResizeObserver(() => {
      // Debounce resize updates
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateAllGrids();
      }, 150);
    });
    
    // Observe each grid for size changes
    this.grids.forEach(grid => {
      this.resizeObserver.observe(grid);
    });
  }

  updateAllGrids() {
    this.grids.forEach(grid => {
      this.updateGrid(grid);
    });
  }

  updateGrid(grid) {
    const items = grid.querySelectorAll('.product-grid__item');
    if (!items.length) return;
    
    // Remove all existing classes first
    items.forEach(item => {
      item.classList.remove(this.FIRST_IN_ROW_CLASS);
    });
    
    // Get the number of columns by checking item positions
    const columnsPerRow = this.getColumnsPerRow(grid, items);
    if (!columnsPerRow) return;
    
    // Apply class to first item in each row
    items.forEach((item, index) => {
      if (index % columnsPerRow === 0) {
        item.classList.add(this.FIRST_IN_ROW_CLASS);
      }
    });
  }

  getColumnsPerRow(grid, items) {
    if (items.length < 2) return 1;
    
    // Get the position of the first two items
    const firstRect = items[0].getBoundingClientRect();
    const secondRect = items[1].getBoundingClientRect();
    
    // If they're on different rows, we have 1 column
    if (Math.abs(firstRect.top - secondRect.top) > 5) {
      return 1;
    }
    
    // Find the first item that's on a different row
    let columnsPerRow = 1;
    const firstTop = firstRect.top;
    
    for (let i = 1; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      
      if (Math.abs(rect.top - firstTop) > 5) {
        // Found first item on second row
        columnsPerRow = i;
        break;
      }
    }
    
    // If all items are on same row
    if (columnsPerRow === 1 && items.length > 1) {
      columnsPerRow = items.length;
    }
    
    return columnsPerRow;
  }

  observeGridChanges() {
    if (!window.MutationObserver) return;
    
    // Observe each grid for DOM changes
    const observer = new MutationObserver(() => {
      // Re-find grids in case new ones were added
      this.findProductGrids();
      this.updateAllGrids();
    });
    
    // Observe the main collection wrapper for changes
    const collectionWrappers = document.querySelectorAll(
      `.${this.FULL_WIDTH_WRAPPER_CLASS}, .${this.FULL_WIDTH_MOBILE_CLASS}`
    );
    
    collectionWrappers.forEach(wrapper => {
      observer.observe(wrapper, {
        childList: true,
        subtree: true
      });
    });
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearTimeout(this.resizeTimeout);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productGridDynamicPadding = new ProductGridDynamicPadding();
  });
} else {
  window.productGridDynamicPadding = new ProductGridDynamicPadding();
}
